import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ReactComponent as PlanneryLogo } from 'images/plannery-logo.svg';
import appStyles from 'prototypes/securedCard/app.module.scss';
import PaycheckControl from 'prototypes/securedCard/components/PaycheckControl';
import PaycheckStack from 'prototypes/securedCard/components/PaycheckStack';
import { formatMoney } from 'prototypes/securedCard/formatMoney';
import styles from 'prototypes/securedCard/landing.module.scss';
import { pathForMilestone, PrototypePath } from 'prototypes/securedCard/paths';
import {
  CASHBACK_LABEL,
  CASHBACK_PERCENT,
  DEFAULT_PAYCHECK_CENTS,
  DEFAULT_PAYCHECK_COUNT,
  SAVINGS_APY_LABEL,
  SAVINGS_APY_PERCENT,
  SPENDABLE_PERCENT,
} from 'prototypes/securedCard/prototypeAssumptions';
import { cadencePhrase, projectStory } from 'prototypes/securedCard/storyMath';
import { useStory } from 'prototypes/securedCard/StoryContext';
import { PayCadence, StoryMilestone } from 'prototypes/securedCard/types';

type PreviewStep = 'payday' | 'stacks' | 'spend' | 'keep';

const PREVIEW_STEPS: { id: PreviewStep; index: string; name: string }[] = [
  { id: 'payday', index: '01', name: 'Each payday' },
  { id: 'stacks', index: '02', name: 'It stacks' },
  { id: 'spend', index: '03', name: 'You spend' },
  { id: 'keep', index: '04', name: 'You keep' },
];

const FAQS = [
  {
    question: 'Can this card put me in debt?',
    answer:
      'Not in this design. The card spends only what your savings can cover, and the purchase is paid from that same account. There is no revolving balance.',
  },
  {
    question: 'Why send my paycheck here?',
    answer:
      'It is the same kind of payroll deduction as a Plannery loan. The money is still yours. It becomes savings, most of it is ready to spend, and you do not have to move it yourself. Paying the card on time is part of the same step.',
  },
  {
    question: 'Do I need a credit score?',
    answer:
      'No score minimum, and no credit check to open it. The prototype is for Plannery customers. Other eligibility rules are not decided.',
  },
  {
    question: 'What if I do not spend it all?',
    answer:
      'It stays saved. The next paycheck adds to it, and the card limit rises with the balance. You are accumulating savings, not resetting every month.',
  },
  {
    question: 'What do I earn?',
    answer: `Savings earn ${SAVINGS_APY_LABEL}, in the range of a high-yield account such as Chime, and a rate this card could offer. Spending earns ${CASHBACK_LABEL}, a typical flat cash-back rate. Cashback is tracked as rewards.`,
  },
  {
    question: 'How does this build credit?',
    answer:
      'The sample purchase is paid from savings automatically. That on-time payment is what this design would report. Which bureau reports it is not decided. This prototype does not show a credit score.',
  },
];

const SellPage = () => {
  const navigate = useNavigate();
  const { state, begin } = useStory();
  const [cents, setCents] = useState(DEFAULT_PAYCHECK_CENTS);
  const [cadence, setCadence] = useState(PayCadence.Biweekly);
  const [paycheckCount, setPaycheckCount] = useState(DEFAULT_PAYCHECK_COUNT);
  const [previewStep, setPreviewStep] = useState<PreviewStep>('payday');
  const projection = projectStory({
    perPaycheckCents: cents,
    cadence,
    paycheckCount: previewStep === 'payday' ? 1 : paycheckCount,
  });
  const alreadyStarted = state.milestone !== StoryMilestone.NotStarted;

  const start = () => {
    begin(cents, cadence, paycheckCount);
    navigate(PrototypePath.Paycheck);
  };

  return (
    <div className={styles.page} data-testid="secured-card-sell">
      <p className={styles.banner}>Design prototype · Mock data · Savings earn 5% APY</p>
      <header className={styles.topBar}>
        <PlanneryLogo className={styles.logo} aria-label="Plannery" />
        <div className={styles.topActions}>
          <a className={styles.topLink} href="#how-it-works">
            How it works
          </a>
          {alreadyStarted && (
            <Link className={styles.topLink} to={pathForMilestone(state.milestone)}>
              Continue
            </Link>
          )}
        </div>
      </header>
      <div className={styles.wrap}>
        <section className={styles.hero}>
          <p className={styles.eyebrow}>Secured card for Plannery customers</p>
          <h1 className={styles.heroTitle}>Your paycheck builds a card. The card never builds debt.</h1>
          <p className={styles.heroBody}>
            Set a payroll deduction, the same way a Plannery loan is paid. Every payday that money becomes savings, and
            most of it becomes spending power. The next payday adds more, so the line grows. Purchases pay themselves
            from the account.
            <sup className={styles.sup}>
              <a href="#disclosures">1</a>
            </sup>
          </p>
          <div className={styles.heroActions}>
            <button type="button" className={styles.heroPrimary} onClick={start}>
              See it step by step
            </button>
            <a className={styles.heroSecondary} href="#how-it-works">
              Watch a paycheck land
            </a>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>What you get</h2>
          <div className={styles.benefitGrid}>
            <article className={styles.benefitCard}>
              <p className={styles.dipLabel}>Save on autopilot</p>
              <h3 className={styles.dipTitle}>One deduction. Every payday.</h3>
              <p className={styles.sectionBody}>
                You are not opening another account to manage. Payroll sends the money, the same way it pays a loan.
              </p>
            </article>
            <article className={styles.benefitCard}>
              <p className={styles.dipLabel}>A line that grows</p>
              <h3 className={styles.dipTitle}>$500 in. $400 to spend.</h3>
              <p className={styles.sectionBody}>
                {SPENDABLE_PERCENT}% of each deposit is ready on the card. The rest stays saved. The following payday
                does it again.
              </p>
            </article>
            <article className={styles.benefitCard}>
              <p className={styles.dipLabel}>Paid on time</p>
              <h3 className={styles.dipTitle}>The card pays itself.</h3>
              <p className={styles.sectionBody}>
                Spending comes out of the savings that are already there. On-time is the default, and you cannot spend
                past what you have saved.
              </p>
            </article>
          </div>
        </section>

        <section className={styles.section} id="how-it-works">
          <h2 className={styles.sectionTitle}>How a paycheck becomes a card</h2>
          <p className={styles.sectionBody}>
            Savings earn {SAVINGS_APY_LABEL}. Spending earns {CASHBACK_LABEL}, a typical flat cash-back rate.
          </p>
          <div className={styles.walkthrough}>
            <div className={styles.stepList}>
              {PREVIEW_STEPS.map((step) => (
                <button
                  key={step.id}
                  type="button"
                  className={
                    previewStep === step.id ? `${styles.stepButton} ${styles.stepButtonOn}` : styles.stepButton
                  }
                  aria-pressed={previewStep === step.id}
                  onClick={() => setPreviewStep(step.id)}
                >
                  <p className={styles.stepIndex}>{step.index}</p>
                  <p className={styles.stepName}>{step.name}</p>
                </button>
              ))}
            </div>
            <div className={styles.phone}>
              <div className={styles.phoneBar}>
                <span className={styles.phoneTitle}>Sample paycheck</span>
                <span className={styles.phoneTitle}>{cadencePhrase(cadence)}</span>
              </div>
              <div className={styles.phoneBody}>
                <PaycheckControl
                  cents={cents}
                  cadence={cadence}
                  idPrefix="preview"
                  onCents={setCents}
                  onCadence={setCadence}
                />
                {previewStep === 'payday' && (
                  <div className={appStyles.figureList}>
                    <div className={`${appStyles.figure} ${appStyles.figurePrimary}`}>
                      <div>
                        <p className={appStyles.figureLabel}>Ready on the card</p>
                        <p className={appStyles.figureHint}>From this payday</p>
                      </div>
                      <p className={appStyles.figureValue}>{formatMoney(projection.availableCents)}</p>
                    </div>
                    <div className={appStyles.figure}>
                      <div>
                        <p className={appStyles.figureLabel}>Stays saved</p>
                      </div>
                      <p className={appStyles.figureValue}>{formatMoney(projection.heldCents)}</p>
                    </div>
                  </div>
                )}
                {previewStep === 'stacks' && (
                  <PaycheckStack paychecks={projection.paychecks} count={paycheckCount} onCount={setPaycheckCount} />
                )}
                {previewStep === 'spend' && (
                  <div className={appStyles.stack}>
                    <div className={appStyles.ledgerRow}>
                      <div>
                        <p className={appStyles.ledgerTitle}>Grocery run</p>
                        <p className={appStyles.ledgerDetail}>Paid from savings. No bill left over.</p>
                      </div>
                      <p className={appStyles.amountOut}>{formatMoney(projection.purchaseCents)}</p>
                    </div>
                    <div className={`${appStyles.callout} ${appStyles.calloutGreen}`}>
                      <p className={appStyles.calloutTitle}>Paid on time</p>
                      <p className={appStyles.calloutBody}>
                        Savings left: {formatMoney(projection.savingsAfterPaymentCents)}. Still on the card:{' '}
                        {formatMoney(projection.availableAfterPaymentCents)}.
                      </p>
                    </div>
                  </div>
                )}
                {previewStep === 'keep' && (
                  <div className={appStyles.figureList}>
                    <div className={appStyles.figure}>
                      <p className={appStyles.figureLabel}>Interest</p>
                      <p className={appStyles.figureValue}>{formatMoney(projection.interestCents)}</p>
                    </div>
                    <div className={appStyles.figure}>
                      <p className={appStyles.figureLabel}>Cashback</p>
                      <p className={appStyles.figureValue}>{formatMoney(projection.cashbackCents)}</p>
                    </div>
                    <div className={`${appStyles.figure} ${appStyles.figurePrimary}`}>
                      <div>
                        <p className={appStyles.figureLabel}>Card after the next payday</p>
                      </div>
                      <p className={appStyles.figureValue}>{formatMoney(projection.nextAvailableCents)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.band}>
          <h2 className={styles.bandTitle}>No score minimum. No credit check to open it.</h2>
          <p className={styles.heroBody}>
            Built for Plannery customers. The deposit is your own pay. The payment history comes from paying that card
            with money you already set aside.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Questions</h2>
          <div className={styles.faqList}>
            {FAQS.map((item) => (
              <details key={item.question} className={styles.faq}>
                <summary className={styles.faqSummary}>{item.question}</summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.band}>
          <h2 className={styles.bandTitle}>See the $500 paycheck, one step at a time.</h2>
          <div className={styles.heroActions}>
            <button type="button" className={styles.heroPrimary} onClick={start}>
              See it step by step
            </button>
          </div>
        </section>

        <section className={styles.footnotes} id="disclosures">
          <h2 className={styles.footnoteTitle}>Disclosures</h2>
          <ol className={styles.footnoteList}>
            <li>
              This is a design prototype. It does not submit an application, check credit, or open an account. Nothing
              here is an offer of credit.
            </li>
            <li>
              {SAVINGS_APY_PERCENT}% APY is the savings yield in this prototype. It is in the range of what a high-yield
              account such as Chime pays, and a rate this card could offer. Interest is simple interest on the savings
              balance after each payday, for that pay period, and it is shown as earnings rather than mixed into the
              spending line.
            </li>
            <li>
              {CASHBACK_PERCENT}% cashback is a typical flat unlimited cash-back rate, in the range of cards such as
              Citi Double Cash and Wells Fargo Active Cash. It is a stand-in for this prototype, not a rewards program.
              Cashback is tracked as rewards and is not added to savings here.
            </li>
            <li>
              {SPENDABLE_PERCENT}% of savings is available to spend. The other {100 - SPENDABLE_PERCENT}% stays in the
              account. A $500 payroll deduction makes $400 available.
            </li>
            <li>
              The sample purchase is a $120 grocery run, or less when the available line is smaller. It is paid from
              savings. The card does not spend past that line.
            </li>
            <li>
              “Cannot put you into debt” describes this design. It is not a legal commitment. Reporting to credit
              bureaus, and which bureau, is not decided.
            </li>
          </ol>
        </section>
      </div>
    </div>
  );
};

export default SellPage;
